import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Search, Menu, X, User, LogOut, Settings, LayoutDashboard, Music, Radio, Calendar, Trophy, Building2, ChevronDown, Video, Mic2, Users, Zap, Medal, ListMusic, Gift, Home, Film, Command, Star, Trash2, Plus, Shield, Heart, MessageCircle, Activity, Megaphone, UserMinus, Briefcase, TrendingUp, Crown, DollarSign, Newspaper, Sun, Moon, Wallet } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { GlobalSearch } from './GlobalSearch';
import { IconLogo } from './Logo';
import NotificationDropdown from './NotificationDropdown';
import { NavbarAvatar } from './UserAvatar';
import { useTheme } from '../context/ThemeContext';
import { getProfileUrl } from '../utils/profileUtils';
import { DonateButton } from './DonateModal';
import { useUserLocation } from '../context/LocationContext';
import { MapPin } from 'lucide-react';

function ThemeToggleItem() {
  const { theme, setTheme } = useTheme();
  const isLight = theme === 'light';
  return (
    <DropdownMenuItem
      onClick={() => setTheme(isLight ? 'dark' : 'light')}
      data-testid="theme-toggle"
    >
      {isLight ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
      {isLight ? 'Dark Mode' : 'Light Mode'}
    </DropdownMenuItem>
  );
}

export const Navbar = () => {
  const { user, logout, isAuthenticated, isArtist, userRole, isPlatformOwner, token } = useAuth();
  const { displayLabel, openLocationPicker } = useUserLocation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [newEarnings, setNewEarnings] = useState({ count: 0, amount: 0 });

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadMessages(data.total || 0);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  const fetchNewEarnings = useCallback(async () => {
    if (!token || !isArtist) return;
    try {
      const res = await fetch(`${API}/earnings/new-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNewEarnings({ count: data.new_count || 0, amount: data.new_amount || 0 });
      }
    } catch (e) { /* silent */ }
  }, [token, isArtist]);

  const markEarningsSeen = useCallback(async () => {
    if (!token) return;
    try {
      await fetch(`${API}/earnings/mark-seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewEarnings({ count: 0, amount: 0 });
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUnreadCount();
    fetchNewEarnings();
    const interval = setInterval(() => { fetchUnreadCount(); fetchNewEarnings(); }, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount, fetchNewEarnings]);

  // Check if a path is currently active
  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Check if on profile page
  const isOnProfilePage = () => {
    if (!user?.handle) return false;
    const profilePaths = [`/u/${user.handle}`, `/profile/${user.handle}`];
    return profilePaths.some(p => location.pathname === p || location.pathname.toLowerCase() === p.toLowerCase());
  };

  // Check if on dashboard
  const isOnDashboard = () => {
    return location.pathname.startsWith('/dashboard');
  };

  // Check if on settings
  const isOnSettings = () => {
    return location.pathname === '/settings';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/home';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Get role-based dashboard path
  const getDashboardPath = () => {
    const roleRedirects = {
      listener: '/dashboard/listener',
      artist: '/dashboard/artist',
      content_creator: '/dashboard/content-creator',
      podcaster: '/dashboard/podcaster',
      streamer: '/dashboard/streamer',
      studio: '/dashboard/studio',
      videographer: '/dashboard/videographer',
      engineer: '/dashboard/engineer',
      graphic_designer: '/dashboard/graphic-designer',
      producer: '/dashboard/producer',
      owner: '/dashboard/artist'
    };
    return roleRedirects[userRole] || '/dashboard/listener';
  };

  // Primary nav — always visible on lg+, focused on music / videos / social / monetization
  const primaryLinks = [
    { href: '/soundstage-spotlight', label: 'Spotlight', icon: Newspaper },
    { href: '/feed', label: 'Feed', icon: Home, requiresAuth: true },
    { href: '/pulse', label: 'Pulse', icon: Activity },
    { href: '/releases', label: 'New Releases', icon: Music },
    { href: '/reels', label: 'Clips', icon: Film },
    { href: '/live', label: 'Live', icon: Zap, isLive: true },
    { href: '/charts', label: 'Charts', icon: Trophy },
    { href: '/creator-subscriptions', label: 'Subscriptions', icon: Crown },
    { href: '/perks', label: 'Perks', icon: Gift },
  ];

  // Secondary — shown in a "More" dropdown
  const secondaryLinks = [
    { href: '/home', label: 'Discover', icon: Music },
    { href: '/playlists', label: 'Playlists', icon: ListMusic },
    { href: '/battle-bot', label: 'Battle Bot', icon: Mic2 },
    { href: '/battle-room', label: 'Battle Room', icon: Radio, requiresAuth: true },
    { href: '/battle-leaderboard', label: 'Battles', icon: Medal },
    { href: '/viral-engine', label: 'Viral Engine', icon: TrendingUp },
    { href: '/content-locks', label: 'Content Locks', icon: Shield },
    { href: '/interviews', label: 'Interviews', icon: Video },
    { href: '/marketplace', label: 'Creatives', icon: Users },
    { href: '/events', label: 'Events', icon: Calendar },
    { href: '/hire-board', label: 'Hire Board', icon: Briefcase },
  ];

  const navLinks = [...primaryLinks, ...secondaryLinks];

  return (
    <nav className="sticky top-0 z-50 glass border-b border-border">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo - Using IconLogo from branding system with responsive sizing */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <IconLogo size="lg" className="rounded-md w-12 h-12 md:w-14 md:h-14" />
            <span className="font-heading font-bold text-lg md:text-xl uppercase tracking-tight hidden sm:block">
              DMV
            </span>
          </Link>

          {/* Location Chip */}
          <button
            onClick={openLocationPicker}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/40 hover:bg-muted/70 border border-border/50 text-xs font-medium text-muted-foreground hover:text-foreground transition-all"
            data-testid="nav-location-chip"
          >
            <MapPin className="w-3 h-3 text-secondary" />
            <span className="max-w-[120px] truncate">{displayLabel || 'Set location'}</span>
          </button>

          {/* Desktop Nav Links — primary only, tightly packed, no wrap */}
          <div className="hidden lg:flex items-center gap-0.5">
            {primaryLinks
              .filter(link => !link.requiresAuth || isAuthenticated)
              .map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  link.isLive 
                    ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.isLive && <link.icon className="w-4 h-4 animate-pulse" />}
                {link.label}
              </Link>
            ))}
            {/* More dropdown for secondary links */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors flex items-center gap-1 whitespace-nowrap text-muted-foreground hover:text-foreground hover:bg-muted/30"
                  data-testid="nav-more-trigger"
                >
                  More <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {secondaryLinks
                  .filter(link => !link.requiresAuth || isAuthenticated)
                  .map((link) => (
                  <DropdownMenuItem
                    key={link.href}
                    onClick={() => navigate(link.href)}
                    data-testid={`nav-more-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon className="w-4 h-4 mr-2" />
                    {link.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Search Bar and Global Navigation Search */}
          <div className="hidden md:flex items-center flex-1 max-w-lg mx-8 gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search artists, tracks, events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted/50 border-border focus:border-primary/50"
                data-testid="nav-search-input"
              />
            </form>
            <GlobalSearch className="shrink-0" />
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {/* Donate Button - Always visible */}
            <DonateButton 
              variant="ghost" 
              size="sm" 
              className="hidden md:flex text-red-400 hover:text-red-300 hover:bg-red-500/10"
            />
            
            {isAuthenticated ? (
              <>
                {/* Messages Icon */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/messages')}
                  className="relative text-muted-foreground hover:text-foreground"
                  data-testid="messages-nav-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1" data-testid="unread-messages-badge">
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </Button>
                
                {/* Notifications Bell */}
                <NotificationDropdown />
                
                {/* User Menu */}
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <NavbarAvatar user={user} />
                    <span className="hidden sm:block text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">@{user?.handle || 'handle'}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem 
                    onClick={() => navigate(getDashboardPath())} 
                    data-testid="menu-dashboard"
                    className={cn(isOnDashboard() && "bg-primary/10 text-primary")}
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                    {isOnDashboard() && <span className="ml-auto text-xs">•</span>}
                  </DropdownMenuItem>
                  {user?.handle && (
                    <DropdownMenuItem 
                      onClick={() => navigate(getProfileUrl(user))} 
                      data-testid="menu-profile"
                      className={cn(isOnProfilePage() && "bg-primary/10 text-primary")}
                    >
                      <User className="w-4 h-4 mr-2" />
                      My Profile
                      {isOnProfilePage() && <span className="ml-auto text-xs">•</span>}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={() => navigate('/creator/wallet')} 
                    data-testid="menu-wallet"
                    className={cn(location.pathname === '/creator/wallet' && "bg-primary/10 text-primary")}
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Wallet
                    {location.pathname === '/creator/wallet' && <span className="ml-auto text-xs">&#8226;</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/coins')}
                    data-testid="menu-coins"
                    className={cn(location.pathname === '/coins' && "bg-primary/10 text-primary")}
                  >
                    <Gift className="w-4 h-4 mr-2 text-amber-400" />
                    Coins & Gifts
                    {location.pathname === '/coins' && <span className="ml-auto text-xs">&#8226;</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/studio-plus')}
                    data-testid="menu-studio-plus"
                    className={cn(location.pathname === '/studio-plus' && "bg-primary/10 text-primary")}
                  >
                    <Crown className="w-4 h-4 mr-2 text-amber-400" />
                    Studio+
                    <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-amber-400">Pro</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => { navigate('/creator/earnings'); markEarningsSeen(); }} 
                    data-testid="menu-earnings"
                    className={cn(location.pathname === '/creator/earnings' && "bg-primary/10 text-primary")}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Earnings
                    {newEarnings.count > 0 ? (
                      <span className="ml-auto flex items-center gap-1">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-[10px] font-bold text-green-500">+${newEarnings.amount.toFixed(2)}</span>
                      </span>
                    ) : (
                      location.pathname === '/creator/earnings' && <span className="ml-auto text-xs">&#8226;</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')} 
                    data-testid="menu-settings"
                    className={cn(isOnSettings() && "bg-primary/10 text-primary")}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                    {isOnSettings() && <span className="ml-auto text-xs">•</span>}
                  </DropdownMenuItem>
                  <ThemeToggleItem />
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem 
                    onClick={() => navigate('/post/create')} 
                    data-testid="menu-create-post"
                    className="text-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Post
                  </DropdownMenuItem>
                  {/* Admin Links - Only for Platform Owner */}
                  {isPlatformOwner && (
                    <>
                      <DropdownMenuSeparator className="bg-border" />
                      <div className="px-2 py-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Admin</p>
                      </div>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/control-center')}
                        data-testid="menu-admin-panel"
                        className="text-purple-400"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/monetization')}
                        data-testid="menu-admin-monetization"
                        className="text-emerald-400"
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        Monetization Control
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/featured')}>
                        <Star className="w-4 h-4 mr-2 text-yellow-500" />
                        Featured Content
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/spotlight')}>
                        <Star className="w-4 h-4 mr-2 text-purple-500" />
                        Community Spotlight
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/roles')}>
                        <Users className="w-4 h-4 mr-2 text-cyan-500" />
                        Admin Roles
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/battles')}>
                        <Radio className="w-4 h-4 mr-2 text-orange-500" />
                        Cypher Battles
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/grant-tier')}>
                        <Users className="w-4 h-4 mr-2 text-blue-500" />
                        Grant Tier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/analytics')}>
                        <Trophy className="w-4 h-4 mr-2 text-cyan-500" />
                        Analytics
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/support-tickets')}>
                        <Settings className="w-4 h-4 mr-2 text-green-500" />
                        Support Tickets
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/content-wipe')}>
                        <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                        Content Reset
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/automation')}>
                        <Activity className="w-4 h-4 mr-2 text-orange-500" />
                        Automation Center
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/announcements')}>
                        <Megaphone className="w-4 h-4 mr-2 text-amber-500" />
                        Announcements
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/cleanup')}>
                        <UserMinus className="w-4 h-4 mr-2 text-red-400" />
                        User Cleanup
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-border" />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="nav-login-btn"
                >
                  Log In
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-secondary hover:bg-secondary/90 text-foreground font-bold uppercase tracking-wider text-sm"
                  data-testid="nav-signup-btn"
                >
                  Sign Up
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-muted/50 border-border"
                />
              </div>
            </form>
            <div className="space-y-1">
              <p className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground/50 font-semibold">Main</p>
              {primaryLinks
                .filter(link => !link.requiresAuth || isAuthenticated)
                .map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md ${
                    link.isLive
                      ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  <link.icon className={`w-4 h-4 ${link.isLive ? 'animate-pulse' : ''}`} />
                  {link.label}
                </Link>
              ))}
              <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground/50 font-semibold">More</p>
              {secondaryLinks
                .filter(link => !link.requiresAuth || isAuthenticated)
                .map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/30"
                  data-testid={`mobile-nav-more-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
              {/* Mobile Donate Button */}
              <div className="px-3 py-2">
                <DonateButton 
                  variant="outline" 
                  size="sm" 
                  className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
